import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { groupService, expenseService, userService, inviteService, inviteLinkService, paymentService, authService } from '../../services/api';
import AppAlert from '../../components/AppAlert';
import ConfirmDialog from '../../components/ConfirmDialog';
import './GroupDetailPage.css';

/**
 * Página de detalhes do grupo com design moderno.
 */
const GroupDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('expenses');
  const [pendingConfirmationCount, setPendingConfirmationCount] = useState(0);

  // Estado para mensagem de sucesso ao ingressar via link
  const [successMessage, setSuccessMessage] = useState(location.state?.message || '');

  // Estado do link de convite
  const [inviteLink, setInviteLink] = useState(null);
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Estado do modal de convite
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Estado do modal de edição de despesa
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({ description: '', amount: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Estado de exclusão/arquivamento
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmar',
    cancelLabel: 'Cancelar',
    variant: 'warning',
    loading: false,
    onConfirm: null,
  });

  useEffect(() => {
    loadGroupData();
    loadPendingConfirmationCount();

    // Atualizar contador a cada 30 segundos
    const interval = setInterval(loadPendingConfirmationCount, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const loadPendingConfirmationCount = async () => {
    try {
      const count = await paymentService.countAwaitingConfirmationByGroup(id);
      console.log(`[GroupDetailPage] Pagamentos pendentes no grupo ${id}:`, count);
      setPendingConfirmationCount(count);
    } catch (err) {
      console.error('Erro ao carregar contador de pagamentos pendentes:', err);
      // Em caso de erro, define como 0 para não quebrar a UI
      setPendingConfirmationCount(0);
    }
  };

  // Effect para gerenciar overflow do body quando modal está aberto
  useEffect(() => {
    if (showInviteLinkModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showInviteLinkModal]);

  const loadGroupData = async () => {
    try {
      const [groupData, membersData, expensesData, invitesData] = await Promise.all([
        groupService.getById(id),
        groupService.getMembers(id),
        expenseService.getByGroup(id, 0, 20),
        inviteService.getByGroup(id),
      ]);
      setGroup(groupData);
      setMembers(membersData);
      setExpenses(expensesData.content || []);
      setPendingInvites(invitesData);

      // Carrega link de convite ativo, se existir
      loadInviteLink();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadInviteLink = async () => {
    try {
      const links = await inviteLinkService.getByGroup(id);
      if (links && links.length > 0) {
        // Pega o primeiro link ativo
        setInviteLink(links[0]);
      }
    } catch (err) {
      console.error('Erro ao carregar link de convite:', err);
    }
  };

  const handleGenerateInviteLink = async () => {
    try {
      setGeneratingLink(true);
      const newLink = await inviteLinkService.create(id);
      setInviteLink(newLink);
      setShowInviteLinkModal(true);
    } catch (err) {
      console.error('Erro ao gerar link:', err);
    } finally {
      setGeneratingLink(false);
    }
  };

  const handleCopyInviteLink = () => {
    const url = `${window.location.origin}/invite/${inviteLink.inviteToken}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleShareInviteLink = async () => {
    const url = `${window.location.origin}/invite/${inviteLink.inviteToken}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Convite para ${group.name}`,
          text: `Você foi convidado para participar do grupo "${group.name}" no Dividu!`,
          url: url,
        });
      } catch (err) {
        // Usuário cancelou o compartilhamento
        console.log('Compartilhamento cancelado');
      }
    } else {
      // Fallback: copiar link
      handleCopyInviteLink();
    }
  };


  // Buscar usuários por email
  const handleSearchUsers = async () => {
    if (searchEmail.length < 3) return;

    setSearchLoading(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      const users = await userService.searchByEmail(searchEmail);
      console.log('Usuários retornados da API:', users);
      
      // Filtrar usuários que já são membros, têm convite pendente, ou é o próprio usuário
      const memberUserIds = members.map(m => m.userId);
      const pendingUserIds = pendingInvites.map(i => i.invitedUserId);
      const filteredUsers = users.filter(u =>
        u.id !== currentUser.userId && // Não pode convidar a si mesmo
        !memberUserIds.includes(u.id) && 
        !pendingUserIds.includes(u.id)
      );
      
      console.log('IDs dos membros:', memberUserIds);
      console.log('IDs com convite pendente:', pendingUserIds);
      console.log('ID do usuário atual:', currentUser.userId);
      console.log('Usuários filtrados:', filteredUsers);
      
      setSearchResults(filteredUsers);
      
      // Mostra mensagem se não encontrou nenhum usuário disponível
      if (users.length > 0 && filteredUsers.length === 0) {
        setInviteError('Este usuário já é membro do grupo ou já possui um convite pendente.');
      }
    } catch (err) {
      console.error('Erro ao buscar usuários:', err);
      setInviteError('Erro ao buscar usuários');
    } finally {
      setSearchLoading(false);
    }
  };

  // Enviar convite
  const handleSendInvite = async (userId) => {
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');
    try {
      await inviteService.create(id, userId, inviteMessage);
      setInviteSuccess('Convite enviado com sucesso!');
      setSearchResults(searchResults.filter(u => u.id !== userId));
      // Atualizar lista de convites pendentes
      const invitesData = await inviteService.getByGroup(id);
      setPendingInvites(invitesData);
      setInviteMessage('');
    } catch (err) {
      setInviteError(err.response?.data?.message || 'Erro ao enviar convite');
    } finally {
      setInviteLoading(false);
    }
  };

  // Cancelar convite
  const handleCancelInvite = async (inviteId) => {
    try {
      await inviteService.cancel(inviteId);
      setPendingInvites(pendingInvites.filter(i => i.id !== inviteId));
    } catch (err) {
      console.error(err);
    }
  };

  // Abrir modal de edição
  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditForm({
      description: expense.description,
      amount: expense.amount.toString()
    });
    setEditError('');
    setShowEditModal(true);
  };

  // Salvar edição da despesa
  const handleSaveEdit = async () => {
    if (!editForm.description.trim() || !editForm.amount) {
      setEditError('Preencha todos os campos');
      return;
    }

    setEditLoading(true);
    setEditError('');
    try {
      await expenseService.update(editingExpense.id, {
        id: editingExpense.id,
        description: editForm.description,
        amount: parseFloat(editForm.amount),
        groupId: parseInt(id),
        paidByMemberId: editingExpense.payerId || editingExpense.paidByMemberId,
        expenseDate: editingExpense.expenseDate || editingExpense.date,
        participantIds: editingExpense.participantIds || []
      });

      // Atualiza a lista de despesas
      await loadGroupData();
      setShowEditModal(false);
      setEditingExpense(null);
    } catch (err) {
      console.error(err);
      setEditError(err.response?.data?.message || 'Erro ao atualizar despesa');
    } finally {
      setEditLoading(false);
    }
  };

  const openConfirmDialog = ({
    title,
    message,
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    variant = 'warning',
    onConfirm,
  }) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      confirmLabel,
      cancelLabel,
      variant,
      loading: false,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => (prev.loading ? prev : {
      open: false,
      title: '',
      message: '',
      confirmLabel: 'Confirmar',
      cancelLabel: 'Cancelar',
      variant: 'warning',
      loading: false,
      onConfirm: null,
    }));
  };

  const handleConfirmDialogConfirm = async () => {
    if (!confirmDialog.onConfirm || confirmDialog.loading) return;

    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog({
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirmar',
        cancelLabel: 'Cancelar',
        variant: 'warning',
        loading: false,
        onConfirm: null,
      });
    } catch (err) {
      console.error(err);
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // Excluir despesa
  const handleDeleteExpense = (expenseId) => {
    openConfirmDialog({
      title: 'Excluir despesa',
      message: 'Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.',
      confirmLabel: 'Excluir',
      variant: 'danger',
      onConfirm: async () => {
        setDeleteLoading(expenseId);
        try {
          await expenseService.delete(expenseId);
          setExpenses(expenses.filter(e => e.id !== expenseId));
        } catch (err) {
          console.error(err);
          alert('Erro ao excluir despesa');
          throw err;
        } finally {
          setDeleteLoading(null);
        }
      }
    });
  };

  // Arquivar grupo
  const handleArchiveGroup = () => {
    openConfirmDialog({
      title: 'Arquivar grupo',
      message: 'Tem certeza que deseja arquivar este grupo? Ele ficará disponível apenas para leitura.',
      confirmLabel: 'Arquivar',
      variant: 'warning',
      onConfirm: async () => {
        setArchiveLoading(true);
        try {
          await groupService.archive(id);
          alert('Grupo arquivado com sucesso!');
          navigate('/groups');
        } catch (err) {
          console.error(err);
          alert(err.response?.data?.message || 'Erro ao arquivar grupo');
          throw err;
        } finally {
          setArchiveLoading(false);
        }
      }
    });
  };

  // Sair do grupo (para membros não donos)
  const handleLeaveGroup = (memberId) => {
    openConfirmDialog({
      title: 'Sair do grupo',
      message: 'Tem certeza que deseja sair do grupo?',
      confirmLabel: 'Sair do grupo',
      variant: 'warning',
      onConfirm: async () => {
        try {
          await groupService.removeMember(memberId);
          alert('Você saiu do grupo.');
          navigate('/groups');
        } catch (err) {
          console.error(err);
          alert('Erro ao sair do grupo');
          throw err;
        }
      }
    });
  };

  // Remover membro (para o dono)
  const handleRemoveMember = (memberId) => {
    openConfirmDialog({
      title: 'Remover membro',
      message: 'Tem certeza que deseja remover este membro?',
      confirmLabel: 'Remover',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await groupService.removeMember(memberId);
          setMembers(members.filter(m => m.id !== memberId));
        } catch (err) {
          console.error(err);
          alert('Erro ao remover membro');
          throw err;
        }
      }
    });
  };

  // Fechar modal
  const closeInviteModal = () => {
    setShowInviteModal(false);
    setSearchEmail('');
    setSearchResults([]);
    setInviteMessage('');
    setInviteError('');
    setInviteSuccess('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const currentUserId = currentUser?.id || currentUser?.userId;
  const isGroupOwner = group?.ownerId === currentUserId;
  const totalExpensesAmount = expenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0);
  const latestExpense = expenses.reduce((latest, expense) => {
    const expenseDate = expense.expenseDate || expense.date;
    if (!expenseDate) return latest;
    if (!latest) return expense;

    const latestDate = latest.expenseDate || latest.date;
    return new Date(expenseDate) > new Date(latestDate) ? expense : latest;
  }, null);
  const ownerMember = members.find(member => member.userId === group?.ownerId);
  const ownerDisplayName = group?.ownerName || ownerMember?.userName || ownerMember?.nickname || 'Dono do grupo';
  const tabItems = [
    { id: 'expenses', label: 'Despesas', icon: 'fa-receipt', count: expenses.length },
    { id: 'members', label: 'Membros', icon: 'fa-user-group', count: members.length },
    ...(pendingInvites.length > 0
      ? [{ id: 'invites', label: 'Convites', icon: 'fa-envelope', count: pendingInvites.length }]
      : [])
  ];

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Carregando grupo...</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon"><i className="fa-duotone fa-magnifying-glass"></i></div>
          <h3 className="empty-state-title">Grupo não encontrado</h3>
          <Link to="/groups" className="btn btn-primary">
            Voltar para grupos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container group-detail-page">
      {/* Mensagem de sucesso ao ingressar via link */}
      {successMessage && (
        <AppAlert
          variant="success"
          message={successMessage}
          onClose={() => setSuccessMessage('')}
          className="group-detail-alert"
          style={{
            marginBottom: 20,
            animation: 'fadeIn 0.3s ease-out'
          }}
        />
      )}

      {/* Header */}
      <section className="group-hero">
        <div className="group-hero__main">
          <Link to="/groups" className="group-detail-backlink">
            <i className="fa-solid fa-arrow-left"></i>
            Voltar para grupos
          </Link>

          <div className="group-hero__title-row">
            <div className="group-hero__icon" aria-hidden="true">
              <i className="fa-duotone fa-users"></i>
            </div>
            <div className="group-hero__title-block">
              <span className="group-hero__eyebrow">Detalhes do grupo</span>
              <h1 className="group-hero__title">{group.name}</h1>
            </div>
          </div>

          <p className="group-hero__description">
            {group.description || 'Acompanhe despesas, membros e convites em um só lugar com mais contexto e menos cliques.'}
          </p>

          <div className="group-hero__chips">
            <span className="group-chip">
              <i className="fa-solid fa-crown"></i>
              Dono: {ownerDisplayName}
            </span>
            <span className="group-chip">
              <i className="fa-solid fa-user-group"></i>
              {members.length} membro{members.length !== 1 ? 's' : ''}
            </span>
            {pendingInvites.length > 0 && (
              <span className="group-chip group-chip--warning">
                <i className="fa-solid fa-envelope-open-text"></i>
                {pendingInvites.length} convite{pendingInvites.length !== 1 ? 's' : ''} pendente{pendingInvites.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {pendingConfirmationCount > 0 && (
            <Link to="/payments/pending-confirmation" className="group-hero__notice">
              <div className="group-hero__notice-icon">
                <i className="fa-solid fa-bell"></i>
              </div>
              <div className="group-hero__notice-content">
                <strong>{pendingConfirmationCount} pagamento{pendingConfirmationCount > 1 ? 's' : ''} aguardando confirmação</strong>
                <span>Revise os comprovantes pendentes para manter o saldo atualizado.</span>
              </div>
              <i className="fa-solid fa-chevron-right"></i>
            </Link>
          )}
        </div>

        <div className="group-hero__actions" aria-label="Ações do grupo">
          <Link to={`/groups/${id}/expenses/new`} className="btn btn-primary group-hero__action group-hero__action--strong">
            <i className="fa-solid fa-plus"></i>
            <span>Nova Despesa</span>
          </Link>

          <Link to={`/groups/${id}/balance`} className="btn btn-success group-hero__action">
            <i className="fa-solid fa-wallet"></i>
            <span>Ver Saldo</span>
          </Link>

          <button
            onClick={() => setShowInviteModal(true)}
            className="btn btn-primary group-hero__action"
          >
            <i className="fa-solid fa-user-plus"></i>
            <span>Convidar</span>
          </button>

          <button
            onClick={() => {
              if (inviteLink) {
                setShowInviteLinkModal(true);
              } else {
                handleGenerateInviteLink();
              }
            }}
            className="btn btn-success group-hero__action"
            disabled={generatingLink}
            title="Compartilhar link de convite"
          >
            {generatingLink ? (
              <i className="fa-solid fa-spinner fa-spin"></i>
            ) : (
              <i className="fa-solid fa-link"></i>
            )}
            <span>{generatingLink ? 'Gerando...' : 'Link de convite'}</span>
          </button>

          {isGroupOwner && (
            <button
              onClick={handleArchiveGroup}
              className="btn btn-outline-danger group-hero__action"
              disabled={archiveLoading}
              title="Arquivar grupo"
            >
              {archiveLoading ? (
                <i className="fa-solid fa-spinner fa-spin"></i>
              ) : (
                <i className="fa-solid fa-archive"></i>
              )}
              <span>{archiveLoading ? 'Arquivando...' : 'Arquivar grupo'}</span>
            </button>
          )}
        </div>
      </section>

      <section className="group-metrics" aria-label="Resumo do grupo">
        <div className="group-metric-card">
          <div className="group-metric-card__icon">
            <i className="fa-solid fa-receipt"></i>
          </div>
          <div className="group-metric-card__content">
            <span className="group-metric-card__label">Total de despesas</span>
            <strong className="group-metric-card__value">{expenses.length}</strong>
            <small className="group-metric-card__hint">
              Somando {formatCurrency(totalExpensesAmount)}
            </small>
          </div>
        </div>

        <div className="group-metric-card">
          <div className="group-metric-card__icon group-metric-card__icon--secondary">
            <i className="fa-solid fa-user-group"></i>
          </div>
          <div className="group-metric-card__content">
            <span className="group-metric-card__label">Participantes</span>
            <strong className="group-metric-card__value">{members.length}</strong>
            <small className="group-metric-card__hint">
              {pendingInvites.length} convite{pendingInvites.length !== 1 ? 's' : ''} pendente{pendingInvites.length !== 1 ? 's' : ''}
            </small>
          </div>
        </div>

        <div className="group-metric-card">
          <div className="group-metric-card__icon group-metric-card__icon--accent">
            <i className="fa-solid fa-clock-rotate-left"></i>
          </div>
          <div className="group-metric-card__content">
            <span className="group-metric-card__label">Última atividade</span>
            <strong className="group-metric-card__value group-metric-card__value--compact">
              {latestExpense ? formatDate(latestExpense.expenseDate || latestExpense.date) : 'Sem despesas'}
            </strong>
            <small className="group-metric-card__hint">
              {latestExpense ? latestExpense.description : 'Crie a primeira despesa para começar'}
            </small>
          </div>
        </div>

        <div className="group-metric-card">
          <div className="group-metric-card__icon group-metric-card__icon--success">
            <i className="fa-solid fa-circle-check"></i>
          </div>
          <div className="group-metric-card__content">
            <span className="group-metric-card__label">Confirmações pendentes</span>
            <strong className="group-metric-card__value">{pendingConfirmationCount}</strong>
            <small className="group-metric-card__hint">
              {pendingConfirmationCount > 0 ? 'Ação necessária' : 'Tudo em dia'}
            </small>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="group-tabs" role="tablist" aria-label="Seções do grupo">
        {tabItems.map(tab => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`group-tab-${tab.id}`}
            aria-controls={`group-tab-panel-${tab.id}`}
            aria-selected={activeTab === tab.id}
            className={`group-tab ${activeTab === tab.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={`fa-solid ${tab.icon}`}></i>
            <span>{tab.label}</span>
            <span className="group-tab__badge">{tab.count}</span>
          </button>
        ))}
      </div>

      <div className="group-content-shell">

      {/* Content */}
      {activeTab === 'expenses' && (
        <div
          className="group-tab-panel animate-fadeIn"
          role="tabpanel"
          id="group-tab-panel-expenses"
          aria-labelledby="group-tab-expenses"
        >
          <div className="group-panel__header">
            <div>
              <h2 className="group-panel__title">Despesas do grupo</h2>
              <p className="group-panel__subtitle">
                Visualize rapidamente quem pagou, quanto foi gasto e edite itens quando permitido.
              </p>
            </div>
            {expenses.length > 0 && (
              <div className="group-panel__header-meta">
                <span>Total</span>
                <strong>{formatCurrency(totalExpensesAmount)}</strong>
              </div>
            )}
          </div>

          {expenses.length === 0 ? (
            <div className="empty-state group-empty-state">
              <div className="empty-state-icon"><i className="fa-duotone fa-receipt"></i></div>
              <h3 className="empty-state-title">Nenhuma despesa ainda</h3>
              <p className="empty-state-text">
                Adicione a primeira despesa do grupo
              </p>
              <Link to={`/groups/${id}/expenses/new`} className="btn btn-primary">
                <i className="fa-solid fa-plus"></i>
                Adicionar Despesa
              </Link>
            </div>
          ) : (
            <div className="group-list-stack">
              {expenses.map((expense, index) => (
                <div
                  key={expense.id}
                  className="expense-item group-expense-row animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="expense-info group-expense-row__main">
                    <div className="expense-icon group-expense-row__icon"><i className="fa-duotone fa-credit-card"></i></div>
                    <div className="expense-details">
                      <h4>{expense.description}</h4>
                      <span>Pago por {expense.payerName || expense.paidByMemberName}</span>
                      <div className="group-expense-row__meta">
                        <span>
                          <i className="fa-regular fa-calendar"></i>
                          {formatDate(expense.expenseDate || expense.date)}
                        </span>
                        {Array.isArray(expense.participantIds) && expense.participantIds.length > 0 && (
                          <span>
                            <i className="fa-solid fa-users"></i>
                            {expense.participantIds.length} participante{expense.participantIds.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="group-expense-row__side">
                    <div className="expense-amount group-expense-row__amount">
                      <div className="value">{formatCurrency(expense.amount)}</div>
                      <div className="date">
                        {expense.canEdit || expense.canDelete ? 'Gerenciável' : 'Somente leitura'}
                      </div>
                    </div>
                    {/* Botões de ação - só aparecem para quem tem permissão */}
                    {(expense.canEdit || expense.canDelete) && (
                      <div className="group-expense-row__actions">
                        {expense.canEdit && (
                          <button
                            onClick={() => handleEditExpense(expense)}
                            className="group-icon-btn"
                            title="Editar despesa"
                            type="button"
                          >
                            <i className="fa-solid fa-pen"></i>
                          </button>
                        )}
                        {expense.canDelete && (
                          <button
                            onClick={() => handleDeleteExpense(expense.id)}
                            disabled={deleteLoading === expense.id}
                            className="group-icon-btn group-icon-btn--danger"
                            title="Excluir despesa"
                            type="button"
                          >
                            {deleteLoading === expense.id ? (
                              <i className="fa-solid fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fa-solid fa-trash"></i>
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div
          className="group-tab-panel animate-fadeIn"
          role="tabpanel"
          id="group-tab-panel-members"
          aria-labelledby="group-tab-members"
        >
          <div className="group-panel__header">
            <div>
              <h2 className="group-panel__title">Membros do grupo</h2>
              <p className="group-panel__subtitle">
                Gerencie participantes, acompanhe quem é dono e mantenha a composição do grupo organizada.
              </p>
            </div>
            <div className="group-panel__header-meta">
              <span>Total</span>
              <strong>{members.length}</strong>
            </div>
          </div>

          {members.length === 0 ? (
            <div className="empty-state group-empty-state">
              <div className="empty-state-icon"><i className="fa-duotone fa-user"></i></div>
              <h3 className="empty-state-title">Nenhum membro no grupo</h3>
            </div>
          ) : (
            <div className="group-list-stack">
              {members.map((member, index) => {
                const isCurrentUser = member.userId === currentUserId;
                const viewerIsOwner = group.ownerId === currentUserId;

                return (
                  <div
                    key={member.id}
                    className="balance-card group-member-row animate-fadeIn"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="balance-user group-member-row__main">
                      <div className="balance-avatar">
                        {getInitials(member.userName || member.nickname)}
                      </div>
                      <div className="group-member-row__details">
                        <div className="group-member-row__name">
                          {member.userName || member.nickname}
                          {member.userId === group.ownerId && (
                            <span className="group-badge group-badge--owner">
                              DONO
                            </span>
                          )}
                          {isCurrentUser && (
                            <span className="group-badge group-badge--self">
                              VOCÊ
                            </span>
                          )}
                        </div>
                        <div className="group-member-row__email">
                          {member.userEmail || member.email}
                        </div>
                      </div>

                      {/* Botão Sair (para o próprio usuário, se não for dono) */}
                      {isCurrentUser && !viewerIsOwner && (
                        <button
                          onClick={() => handleLeaveGroup(member.id)}
                          className="btn btn-outline-danger group-row-action"
                          type="button"
                        >
                          <i className="fa-solid fa-door-open"></i>
                          <span>Sair</span>
                        </button>
                      )}

                      {/* Botão Remover (apenas para o dono, não pode remover a si mesmo aqui) */}
                      {viewerIsOwner && !isCurrentUser && (
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="btn btn-outline-danger group-row-action group-row-action--icon"
                          title="Remover do grupo"
                          type="button"
                        >
                          <i className="fa-solid fa-user-minus"></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Aba de convites pendentes */}
      {activeTab === 'invites' && (
        <div
          className="group-tab-panel animate-fadeIn"
          role="tabpanel"
          id="group-tab-panel-invites"
          aria-labelledby="group-tab-invites"
        >
          <div className="group-panel__header">
            <div>
              <h2 className="group-panel__title">Convites pendentes</h2>
              <p className="group-panel__subtitle">
                Acompanhe quem ainda não entrou no grupo e cancele convites quando necessário.
              </p>
            </div>
            <div className="group-panel__header-meta">
              <span>Pendentes</span>
              <strong>{pendingInvites.length}</strong>
            </div>
          </div>

          {pendingInvites.length === 0 ? (
            <div className="empty-state group-empty-state">
              <div className="empty-state-icon"><i className="fa-duotone fa-envelope"></i></div>
              <h3 className="empty-state-title">Nenhum convite pendente</h3>
            </div>
          ) : (
            <div className="group-list-stack">
              {pendingInvites.map((invite, index) => (
                <div
                  key={invite.id}
                  className="balance-card group-member-row group-invite-row animate-fadeIn"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="balance-user group-member-row__main">
                    <div className="balance-avatar group-invite-row__avatar">
                      <i className="fa-solid fa-clock" style={{ fontSize: 16 }}></i>
                    </div>
                    <div className="group-member-row__details">
                      <div className="group-member-row__name">
                        {invite.invitedUserName}
                        <span className="group-badge group-badge--pending">PENDENTE</span>
                      </div>
                      <div className="group-member-row__email">
                        {invite.invitedUserEmail}
                      </div>
                      <div className="group-invite-row__meta">
                        Convidado por {invite.invitedByName}
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="btn btn-outline-danger group-row-action"
                      type="button"
                    >
                      <i className="fa-solid fa-times"></i>
                      <span>Cancelar</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        onConfirm={handleConfirmDialogConfirm}
        onCancel={closeConfirmDialog}
      />

      {/* Modal de Convite */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeInviteModal}
        >
          <div
            className="card"
            style={{
              width: '100%',
              maxWidth: 500,
              maxHeight: '80vh',
              overflow: 'auto',
              animation: 'fadeIn 0.2s ease-out',
              background: 'var(--bg-primary, #fff)',
              margin: 16,
              padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 24,
            }}>
              <h2 style={{ margin: 0 }}>
                <i className="fa-solid fa-user-plus" style={{ marginRight: 12, color: 'var(--primary-color)' }}></i>
                Convidar Membro
              </h2>
              <button
                onClick={closeInviteModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                }}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* Buscar por email */}
            <div className="form-group">
              <label className="form-label">Buscar por email</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  className="form-input"
                  placeholder="Digite o email do usuário..."
                  value={searchEmail}
                  onChange={e => setSearchEmail(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && handleSearchUsers()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSearchUsers}
                  disabled={searchLoading || searchEmail.length < 3}
                >
                  {searchLoading ? (
                    <i className="fa-solid fa-spinner fa-spin"></i>
                  ) : (
                    <i className="fa-solid fa-search"></i>
                  )}
                </button>
              </div>
              <small style={{ color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>
                Digite pelo menos 3 caracteres
              </small>
            </div>

            {/* Mensagens de erro/sucesso */}
            {inviteError && (
              <AppAlert variant="error" message={inviteError} style={{ marginTop: 16 }} />
            )}
            {inviteSuccess && (
              <AppAlert variant="success" message={inviteSuccess} style={{ marginTop: 16 }} />
            )}

            {/* Resultados da busca */}
            {searchResults.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4 style={{ marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
                  Usuários encontrados
                </h4>
                {searchResults.map(user => (
                  <div
                    key={user.id}
                    className="balance-card"
                    style={{ marginBottom: 8 }}
                  >
                    <div className="balance-user">
                      <div className="balance-avatar">
                        {getInitials(user.name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{user.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {user.email}
                        </div>
                      </div>
                      <button
                        onClick={() => handleSendInvite(user.id)}
                        className="btn btn-primary"
                        disabled={inviteLoading}
                        style={{ padding: '8px 16px' }}
                      >
                        {inviteLoading ? (
                          <i className="fa-solid fa-spinner fa-spin"></i>
                        ) : (
                          <>
                            <i className="fa-solid fa-paper-plane"></i> Convidar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mensagem personalizada (opcional) */}
            <div className="form-group" style={{ marginTop: 24 }}>
              <label className="form-label">Mensagem (opcional)</label>
              <textarea
                className="form-input"
                placeholder="Adicione uma mensagem ao convite..."
                value={inviteMessage}
                onChange={e => setInviteMessage(e.target.value)}
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Despesa */}
      {showEditModal && editingExpense && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="card-modern"
            style={{
              width: '100%',
              maxWidth: 500,
              maxHeight: '80vh',
              overflow: 'auto',
              animation: 'fadeIn 0.2s ease-out',
              background: 'var(--bg-primary, #fff)',
              margin: 16,
              padding: 24,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 24,
            }}>
              <h2 style={{ margin: 0 }}>
                <i className="fa-solid fa-pen" style={{ marginRight: 12, color: 'var(--primary-color)' }}></i>
                Editar Despesa
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                }}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {editError && (
              <AppAlert variant="error" message={editError} style={{ marginBottom: 16 }} />
            )}

            <div className="form-group">
              <label className="form-label">Descrição</label>
              <input
                type="text"
                className="form-input"
                placeholder="Ex: Almoço, Cinema, etc."
                value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Valor (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                placeholder="0,00"
                value={editForm.amount}
                onChange={e => setEditForm({ ...editForm, amount: e.target.value })}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary"
                disabled={editLoading}
                style={{ flex: 1 }}
              >
                {editLoading ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i> Salvando...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check"></i> Salvar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Link de Convite */}
      {showInviteLinkModal && inviteLink && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
          onClick={() => setShowInviteLinkModal(false)}
        >
          <div
            className="card modal-content"
            style={{
              width: '100%',
              maxWidth: 500,
              padding: 24,
              animation: 'fadeIn 0.2s ease-out',
              position: 'relative',
              zIndex: 10000,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 24,
            }}>
              <h2 style={{ margin: 0 }}>
                <i className="fa-solid fa-link" style={{ marginRight: 12, color: '#10b981' }}></i>
                Link de Convite
              </h2>
              <button
                onClick={() => setShowInviteLinkModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Compartilhe este link com as pessoas que você quer convidar para o grupo. 
              Qualquer pessoa com este link poderá ingressar no grupo.
            </p>

            {/* Informações do link */}
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
            }}>
              {inviteLink.maxUses && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: 12,
                  fontSize: 14,
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    <i className="fa-solid fa-users" style={{ marginRight: 8 }}></i>
                    Usos
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {inviteLink.currentUses} / {inviteLink.maxUses}
                  </span>
                </div>
              )}
              
              {inviteLink.expiresAt && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  fontSize: 14,
                }}>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    <i className="fa-solid fa-clock" style={{ marginRight: 8 }}></i>
                    Expira em
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {new Date(inviteLink.expiresAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              )}

              {!inviteLink.maxUses && !inviteLink.expiresAt && (
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>
                  <i className="fa-solid fa-infinity" style={{ marginRight: 8 }}></i>
                  Link sem limite de usos ou expiração
                </div>
              )}
            </div>

            {/* URL do link */}
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              wordBreak: 'break-all',
              fontSize: 14,
              fontFamily: 'monospace',
              color: 'var(--text-secondary)',
            }}>
              {`${window.location.origin}/invite/${inviteLink.inviteToken}`}
            </div>

            {/* Botões de ação */}
            <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
              <button
                onClick={handleCopyInviteLink}
                className="btn btn-primary"
                style={{ width: '100%' }}
              >
                <i className={linkCopied ? "fa-solid fa-check" : "fa-solid fa-copy"}></i>
                {linkCopied ? 'Link Copiado!' : 'Copiar Link'}
              </button>
              
              {navigator.share && (
                <button
                  onClick={handleShareInviteLink}
                  className="btn btn-outline-primary"
                  style={{ width: '100%' }}
                >
                  <i className="fa-solid fa-share-nodes"></i>
                  Compartilhar
                </button>
              )}
            </div>

            {/* Mensagem de sucesso */}
            {successMessage && (
              <AppAlert variant="success" message={successMessage} style={{ marginTop: 16 }} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupDetailPage;
